/* eslint-disable security/detect-object-injection */
/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { ISwatchItem, SwatchComponent } from '../swatch';
import { DimensionTypes } from '@msdyn365-commerce-modules/retail-actions';
import { getPayloadObject, getTelemetryAttributes, IPayLoad } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IDropdownItemProps, IDropdownProps, SelectionType } from './dropdown.props';
import { messageClickHandle } from '../swatch/message-handler';

interface IDropdownState {
    /**
     * @friendlyName selectedIndex
     * @description Selected index.
     */
    selectedIndex: string;
    isColor: boolean;
    isSize: boolean;
    isStyle: boolean;
    productId: number;
    dropdownLength: number;
}

/**
 *
 * DropDown component.
 * @extends {React.PureComponent<IDropdownProps, IDropdownState>}
 */
export class Dropdown extends React.PureComponent<IDropdownProps, IDropdownState> {
    private readonly selectMenu: React.RefObject<HTMLSelectElement> = React.createRef<HTMLSelectElement>();

    private readonly payLoad: IPayLoad;
    private timer: NodeJS.Timeout | null;
    public constructor(props: IDropdownProps, state: IDropdownState) {
        super(props);
        this.timer = null;
        const selectedItem = this.props.dropdownList.find(item => item.isDefault);
        if (selectedItem) {
            this.state = {
                selectedIndex: selectedItem.id.toString(),
                isColor: false,
                isSize: false,
                isStyle: false,
                productId: 0,
                dropdownLength: 0
            };
            if (this.props.onChange) {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises -- Update for default.
                this.props.onChange({
                    dropdownId: this.props.dropdownId,
                    selectId: selectedItem.id,
                    selectedValue: selectedItem.value
                });
            }
        } else {
            this.state = {
                selectedIndex: '',
                isColor: false,
                isSize: false,
                isStyle: false,
                productId: 0,
                dropdownLength: 0
            };
        }
        this.payLoad = getPayloadObject('click', props.telemetryContent!, this.props.dropdownName);
    }
    public componentWillUnmount(): void {
        if (this.timer) {
            clearTimeout(this.timer);
        }
    }

    public render(): JSX.Element {
        if (this.props.apiSettings && this.props.selectionType === SelectionType.swatch) {
            return this._renderSwatch();
        }

        return this._renderDropdown();
    }

    private _renderDropdown(): JSX.Element {
        const { dropdownId, dropdownList, dropdownName, dropdownToggleName } = this.props;
        const attribute = getTelemetryAttributes(this.props.telemetryContent!, this.payLoad);
        return (
            <select
                id={`msc-dropdown__entry-${dropdownId}`}
                ref={this.selectMenu}
                aria-label={dropdownName}
                className='msc-dropdown__select'
                onChange={this._onChanged}
                {...attribute}
            >
                <option value='' aria-selected={this.state.selectedIndex === ''} selected={this.state.selectedIndex === ''} hidden disabled>
                    {dropdownToggleName}
                </option>
                {dropdownList.map(
                    (item: IDropdownItemProps) =>
                        item && (
                            <option
                                value={item.id}
                                key={item.id}
                                selected={this.state.selectedIndex === item.id.toString()}
                                aria-selected={this.state.selectedIndex === item.id.toString()}
                                className={item.isDisabled ? 'msc-dropdown__select__disabled' : undefined}
                            >
                                {item.value}
                            </option>
                        )
                )}
            </select>
        );
    }

    private _renderSwatch(): JSX.Element {
        const swatchItems = this.props.dropdownList.map<ISwatchItem>(item => {
            return {
                ...item,
                itemId: item.id,
                dimensionType: this.props.dropdownName as DimensionTypes
            };
        });
        return (
            <SwatchComponent
                list={swatchItems}
                requestContext={this.props.requestContext}
                apiSettings={this.props.apiSettings!}
                resources={this.props.resources}
                onSelectItem={this._selectSwatch}
                dropdownId={this.props.dropdownId}
                moduleProps={this.props.moduleProps}
            />
        );
    }
    // In Stock message is no longer will be managed by fronted
    // private readonly _showInStockMessage = () => {
    //     if (typeof window !== 'undefined') {
    //         const getMessageElement = document.querySelectorAll('.ms-buybox__backorder-message');
    //         getMessageElement[0].innerHTML = this.props.moduleProps.resources.messageInStock;
    //     }
    // };

    /**
     * Updates state on swatch selection.
     * @param item - Selected swatch item.
     */
    private readonly _selectSwatch = async (item: ISwatchItem) => {
        this.setState({ selectedIndex: item.itemId });

        this.timer = setTimeout(() => {
            if (typeof window !== 'undefined') {
                const colorSelector = document.querySelectorAll('[data-dimension^="Col"], [data-dimension^="F"]');
                const sizeSelector = document.querySelectorAll('[data-dimension^="Size"], [data-dimension^="G"]');
                const styleSelector = document.querySelectorAll('[data-dimension^="Style"]');
                const getDropdown = document.querySelectorAll('.ms-buybox__dropdown');
                this.setState({ dropdownLength: getDropdown.length });

                if (colorSelector && this.state.dropdownLength === 1) {
                    for (let i = 0; i < colorSelector.length; i++) {
                        const checkElement = colorSelector[i].classList.contains('msc-swatch-container__item__selected');
                        if (checkElement && item.variantIds && item.variantIds.ProductIds) {
                            if (
                                colorSelector[i].getAttribute('title')?.toLowerCase() ===
                                item.variantIds.DimensionValue?.Value?.toLowerCase()
                            ) {
                                this.setState({ productId: item.variantIds.ProductIds[0] });
                            }
                        }
                        if (checkElement) {
                            this.setState({ isColor: true });
                        }
                    }
                } else if (colorSelector && sizeSelector && this.state.dropdownLength === 2) {
                    for (let i = 0; i < colorSelector.length; i++) {
                        const checkElement = colorSelector[i].classList.contains('msc-swatch-container__item__selected');
                        if (checkElement && item.variantIds && item.variantIds.ProductIds) {
                            if (
                                colorSelector[i].getAttribute('title')?.toLowerCase() ===
                                item.variantIds.DimensionValue?.Value?.toLowerCase()
                            ) {
                                this.setState({ productId: item.variantIds.ProductIds[0] });
                            }
                        }
                        if (checkElement) {
                            this.setState({ isColor: true });
                        }
                    }
                    for (let i = 0; i < sizeSelector.length; i++) {
                        const checkElement = sizeSelector[i].classList.contains('msc-swatch-container__item__selected');
                        if (checkElement && item.variantIds && item.variantIds.ProductIds) {
                            if (
                                sizeSelector[i].getAttribute('title')?.toLowerCase() ===
                                item.variantIds.DimensionValue?.Value?.toLowerCase()
                            ) {
                                this.setState({ productId: item.variantIds.ProductIds[0] });
                            }
                        }
                        if (checkElement) {
                            this.setState({ isSize: true });
                        }
                    }
                } else if (colorSelector && sizeSelector && styleSelector && this.state.dropdownLength > 2) {
                    for (let i = 0; i < sizeSelector.length; i++) {
                        const checkElement = sizeSelector[i].classList.contains('msc-swatch-container__item__selected');
                        if (checkElement && item.variantIds && item.variantIds.ProductIds) {
                            if (
                                sizeSelector[i].getAttribute('title')?.toLowerCase() ===
                                item.variantIds.DimensionValue?.Value?.toLowerCase()
                            ) {
                                this.setState({ productId: item.variantIds.ProductIds[0] });
                            }
                        }
                        if (checkElement) {
                            this.setState({ isColor: true });
                        }
                    }
                    for (let i = 0; i < sizeSelector.length; i++) {
                        const checkElement = sizeSelector[i].classList.contains('msc-swatch-container__item__selected');
                        if (checkElement && item.variantIds && item.variantIds.ProductIds) {
                            if (
                                sizeSelector[i].getAttribute('title')?.toLowerCase() ===
                                item.variantIds.DimensionValue?.Value?.toLowerCase()
                            ) {
                                this.setState({ productId: item.variantIds.ProductIds[0] });
                            }
                        }
                        if (checkElement) {
                            this.setState({ isSize: true });
                        }
                    }
                    for (let i = 0; i < styleSelector.length; i++) {
                        const checkElement = styleSelector[i].classList.contains('msc-swatch-container__item__selected');
                        if (checkElement && item.variantIds && item.variantIds.ProductIds) {
                            if (
                                styleSelector[i].getAttribute('title')?.toLowerCase() ===
                                item.variantIds.DimensionValue?.Value?.toLowerCase()
                            ) {
                                this.setState({ productId: item.variantIds.ProductIds[0] });
                            }
                        }
                        if (checkElement) {
                            this.setState({ isStyle: true });
                        }
                    }
                }
            }

            if (typeof window !== 'undefined') {
                const getMessageElement = document.querySelectorAll('.ms-buybox__backorder-message');
                if ((item.NB === false && item.inventoryCode === 'OOS') || item.inventoryCode === 'AVAIL') {
                    if (this.state.isColor && this.state.dropdownLength === 1) {
                        messageClickHandle(this.state.productId, this.props.requestContext, this.props.moduleProps.config);
                        getMessageElement[0].setAttribute('style', 'display: block');
                    } else if (this.state.isColor && this.state.isSize && this.state.dropdownLength === 2) {
                        messageClickHandle(this.state.productId, this.props.requestContext, this.props.moduleProps.config);
                        getMessageElement[0].setAttribute('style', 'display: block');
                    } else if (this.state.isColor && this.state.isSize && this.state.isStyle && this.state.dropdownLength === 3) {
                        messageClickHandle(this.state.productId, this.props.requestContext, this.props.moduleProps.config);
                        getMessageElement[0].setAttribute('style', 'display: block');
                    }
                }
                // } else if (item.inventoryCode === 'AVAIL') {
                //     if (this.state.isColor && this.state.dropdownLength === 1) {
                //         this._showInStockMessage();
                //         getMessageElement[0].setAttribute('style', 'display: block');
                //     } else if (this.state.isColor && this.state.isSize && this.state.dropdownLength === 2) {
                //         this._showInStockMessage();
                //         getMessageElement[0].setAttribute('style', 'display: block');
                //     } else if (this.state.isColor && this.state.isSize && this.state.isStyle && this.state.dropdownLength === 3)
                //{
                //         this._showInStockMessage();
                //         getMessageElement[0].setAttribute('style', 'display: block');
                //     }
                // }
            }
        }, 1000);
        if (this.props.onChange) {
            await this.props.onChange({
                dropdownId: this.props.dropdownId,
                selectId: item.itemId,
                selectedValue: item.value
            });
        }
    };

    private readonly _onChanged = async (event: React.ChangeEvent<HTMLSelectElement>) => {
        this.setState({ selectedIndex: event.target.value });
        if (this.props.onChange) {
            await this.props.onChange({
                dropdownId: this.props.dropdownId,
                selectId: event.target.value,
                selectedValue: event.target.innerText
            });
        }
    };
}
